import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import ChipEstado from "./ChipEstado";
import AccionesPedido from "./AccionesPedido";
import DetallePedidoV2 from "./DetallePedidoV2";
import useReparto from "./useRepartos";
import {
  COLOR_MARCA,
  cantidadEntregada,
  datosEstado,
  estaVencido,
  formatearFecha,
  formatearNumero,
} from "./constantes";

// Hasta 2 líneas por texto y "…" si sigue (el texto completo se ve al pasar el mouse).
// Se permite el corte de línea para que el contenido no ensanche la columna.
const textoRecortado = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
  wordBreak: "break-word",
};

// Texto principal + línea secundaria más chica
function DosLineas({ principal, secundaria, colorSecundaria = "text.secondary" }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={textoRecortado}
        title={typeof principal === "string" ? principal : undefined}
      >
        {principal || "—"}
      </Typography>
      {secundaria ? (
        <Typography
          variant="caption"
          color={colorSecundaria}
          sx={textoRecortado}
          title={typeof secundaria === "string" ? secundaria : undefined}
        >
          {secundaria}
        </Typography>
      ) : null}
    </Box>
  );
}

// Reparto: número de planilla (repartos.nro_remito) y chofer · patente
function CeldaReparto({ nroReparto }) {
  const reparto = useReparto(nroReparto);
  if (!nroReparto) return "—";
  if (!reparto) return <Typography variant="caption" color="text.secondary">...</Typography>;
  const chofer = [reparto.nombrepila, reparto.apellido].filter(Boolean).join(" ") || reparto.usuario_choferinicia;
  return (
    <DosLineas
      principal={reparto.nro_remito ? ` ${reparto.nro_remito}` : `Reparto ${reparto.id}`}
      secundaria={[chofer, reparto.patente].filter(Boolean).join(" · ")}
    />
  );
}

// Fecha en formato YYYY-MM-DD para ordenar (las celdas la muestran DD-MM-YYYY)
const fechaOrdenable = (fecha) => (fecha && moment(fecha).isValid() ? moment(fecha).format("YYYY-MM-DD") : "");

// Columnas secundarias que se ocultan según el ancho de pantalla (se pueden volver a mostrar a mano).
// Reparto, sucursal y remito también se ven en el detalle expandido.
const visibilidadPorAncho = (mediano, chico) => ({
  registrado: !mediano,
  nro_reparto: !mediano,
  sucursal_atiende: !chico,
  nro_remito: !chico,
});

function TablaPedidosV2({ pedidos, pestania, cargando, permisos, onAccion, paginacion, onPaginacion }) {
  const pantallaMediana = useMediaQuery("(max-width:1199px)", { noSsr: true });
  const pantallaChica = useMediaQuery("(max-width:899px)", { noSsr: true });
  const [visibilidad, setVisibilidad] = useState(() => ({
    // Empresa se muestra debajo de la sucursal; la columna queda para filtrar
    empresa: false,
    ...visibilidadPorAncho(pantallaMediana, pantallaChica),
  }));

  useEffect(() => {
    setVisibilidad((v) => ({ ...v, ...visibilidadPorAncho(pantallaMediana, pantallaChica) }));
  }, [pantallaMediana, pantallaChica]);

  const columnas = useMemo(() => {
    const lista = [
      {
        accessorKey: "id_pedido",
        header: "#",
        size: 60,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <Typography variant="body2" fontWeight={700}>
              {row.original.id_pedido}
            </Typography>
            {row.original.prioritario ? (
              <Tooltip title="Prioritario" arrow>
                <PriorityHighIcon color="error" sx={{ fontSize: 18 }} />
              </Tooltip>
            ) : null}
          </Box>
        ),
      },
      {
        id: "cliente",
        header: "Cliente",
        size: 170,
        enableColumnFilter: false,
        // Incluye domicilio y localidad para que la búsqueda los encuentre
        accessorFn: (p) =>
          [p.sisven_id, p.cliente, p.direccion, p.lugar, p.localidad_direccionentrega].filter(Boolean).join(" "),
        Cell: ({ row }) => {
          const p = row.original;
          return (
            <DosLineas
              principal={`${p.sisven_id ? p.sisven_id + " - " : ""}${p.cliente || ""}`}
              secundaria={[p.direccion, p.localidad_direccionentrega].filter(Boolean).join(" · ")}
            />
          );
        },
      },
      {
        id: "estado",
        header: "Estado",
        size: 130,
        filterVariant: "select",
        accessorFn: (p) => datosEstado(p.id_estado, p.valor).nombre,
        // El chip puede pasar a dos líneas para no ensanchar la columna
        Cell: ({ row }) => (
          <Box sx={{ "& .MuiChip-root": { height: "auto" }, "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }}>
            <ChipEstado idEstado={row.original.id_estado} valor={row.original.valor} />
          </Box>
        ),
      },
    ];

    if (!pestania.conEntrega) {
      lista.push({
        id: "requerido",
        header: "Requerido",
        size: 95,
        enableColumnFilter: false,
        accessorFn: (p) => fechaOrdenable(p.fecha_requerida),
        Cell: ({ row }) => {
          const p = row.original;
          const vencido = estaVencido(p);
          return (
            <DosLineas
              principal={p.kgacargar ? `${formatearNumero(p.kgacargar)} kg` : "—"}
              secundaria={formatearFecha(p.fecha_requerida)}
              colorSecundaria={vencido ? "error.main" : "text.secondary"}
            />
          );
        },
      });
    }

    lista.push(
      {
        accessorKey: "sucursal_atiende",
        header: "Sucursal",
        size: 110,
        filterVariant: "select",
        Cell: ({ row }) => (
          <DosLineas principal={row.original.sucursal_atiende} secundaria={row.original.empresa} />
        ),
      },
      {
        accessorKey: "empresa",
        header: "Empresa",
        filterVariant: "select",
      },
      {
        id: "registrado",
        header: "Registrado",
        size: 95,
        enableColumnFilter: false,
        accessorFn: (p) => fechaOrdenable(p.registrado),
        Cell: ({ row }) => formatearFecha(row.original.registrado) || "—",
      },
      {
        accessorKey: "nro_reparto",
        header: "Reparto",
        size: 130,
        enableColumnFilter: false,
        // El uuid no se muestra ni se busca: la planilla y el chofer se cargan aparte
        enableGlobalFilter: false,
        Cell: ({ row }) => <CeldaReparto nroReparto={row.original.nro_reparto} />,
      }
    );

    if (pestania.conEntrega) {
      lista.push({
        id: "entrega",
        header: "Entrega",
        size: 95,
        enableColumnFilter: false,
        // Ordena por fecha; la búsqueda global encuentra el remito por su columna
        accessorFn: (p) => fechaOrdenable(p.fecha_real_entrega),
        Cell: ({ row }) => {
          const p = row.original;
          return (
            <DosLineas
              principal={cantidadEntregada(p) || "—"}
              secundaria={formatearFecha(p.fecha_real_entrega)}
            />
          );
        },
      });
      lista.push({
        accessorKey: "nro_remito",
        header: "Remito",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ row }) => row.original.nro_remito || "—",
      });
    }

    if (pestania.facturados) {
      lista.push({
        id: "facturacion",
        header: "Facturado",
        size: 95,
        enableColumnFilter: false,
        accessorFn: (p) => fechaOrdenable(p.fecha_facturacion),
        Cell: ({ row }) => formatearFecha(row.original.fecha_facturacion) || "—",
      });
    }

    return lista;
  }, [pestania]);

  return (
    <MaterialReactTable
      columns={columnas}
      data={pedidos}
      // Mientras carga, la tabla usa filas vacías (sin id_pedido): cada una necesita su propia clave
      getRowId={(p, indice) => (p?.id_pedido != null ? String(p.id_pedido) : `cargando-${indice}`)}
      localization={MRT_Localization_ES}
      enableFacetedValues
      enableDensityToggle={false}
      // Acciones fijada a la derecha: no se mueve entre pestañas y queda visible aunque haya scroll
      enablePinning
      enableExpandAll={false}
      // Sin menú en los encabezados (como la V1): ocupa lugar y fuerza columnas más anchas
      enableColumnActions={false}
      enableRowActions
      positionActionsColumn="last"
      autoResetPageIndex={false}
      positionGlobalFilter="left"
      initialState={{
        density: "compact",
        showGlobalFilter: true,
        showColumnFilters: false,
        sorting: [{ id: "id_pedido", desc: true }],
        columnPinning: { right: ["mrt-row-actions"] },
      }}
      state={{
        isLoading: cargando,
        pagination: paginacion,
        showProgressBars: cargando && pedidos.length > 0,
        columnVisibility: visibilidad,
      }}
      onPaginationChange={onPaginacion}
      onColumnVisibilityChange={setVisibilidad}
      displayColumnDefOptions={{
        "mrt-row-actions": { header: "Acciones", size: 60 },
        "mrt-row-expand": { size: 40 },
      }}
      renderRowActions={({ row }) => (
        <AccionesPedido pedido={row.original} permisos={permisos} onAccion={onAccion} />
      )}
      renderDetailPanel={({ row }) => <DetallePedidoV2 pedido={row.original} />}
      muiSearchTextFieldProps={{
        placeholder: "Buscar por pedido, cliente, domicilio, remito...",
        size: "small",
        variant: "outlined",
        sx: { minWidth: { xs: "14rem", md: "26rem" }, bgcolor: "#fff" },
      }}
      muiTablePaperProps={{ elevation: 0, sx: { border: "1px solid", borderColor: "divider", borderRadius: 2 } }}
      // En densidad compacta la tabla no deja cortar líneas (nowrap) y el texto ensancha las columnas
      muiTableBodyCellProps={{ sx: { whiteSpace: "normal" } }}
      muiTableHeadCellProps={{
        sx: { bgcolor: COLOR_MARCA, color: "#fff", fontWeight: 600, "& .MuiSvgIcon-root": { color: "#fff" } },
      }}
      muiTablePaginationProps={{ rowsPerPageOptions: [10, 25, 50, 100] }}
    />
  );
}

export default TablaPedidosV2;
